// Must be first: connects to the local Firebase Emulator Suite before any
// auth/firestore listener is attached. No-op unless VUE_APP_USE_EMULATORS=true.
import "./firebase/emulators";
import { createApp, defineAsyncComponent } from "vue";
import App from "./App.vue";
import router from "../src/router/index";
import "./index.css";
import "./assets/intercom-custom.css";
import VueGoogleMaps from "@fawmi/vue-google-maps";
import store from "./store";
import "./composables/getUser";
import { onAuthStateChanged } from "firebase/auth";
import { projectAuth } from "./firebase/config";
import { bootstrapAuthSession } from "./composables/bootstrapAuthSession";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import { setDashboardRouter } from "./composables/dashboardNavigation";
import TintBoltBrand from "./components/TintBoltBrand.vue";
import { startAccountUsageTracking } from "./composables/useAccountUsageTracking";

// Benign when slide-overs/dialogs close; avoid dev overlay false positives.
const RESIZE_OBSERVER_LOOP =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;
window.addEventListener(
  "error",
  (event) => {
    if (RESIZE_OBSERVER_LOOP.test(event.message ?? "")) {
      event.stopImmediatePropagation();
    }
  },
  true
);

// Initialize Google Analytics
const initGoogleAnalytics = () => {
  if (typeof window !== "undefined" && window.gtag) {
    if (store.state.currentUser) {
      window.gtag("config", "G-SPJJ1BMYLJ", {
        user_id: store.state.currentUser.uid,
        user_email: store.state.currentUser.email,
      });
    }
  }
};

/** First navigation runs before IndexedDB session restore unless we wait here. */
function waitForInitialAuthState() {
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(projectAuth, () => {
      unsub();
      resolve();
    });
  });
}

const app = createApp(App);

app.use(store);
app.use(router);
startAccountUsageTracking();
setDashboardRouter(router);
app.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
    libraries: "places",
  },
});
app.component(
  "lottie-animation",
  defineAsyncComponent(() =>
    import(/* webpackChunkName: "lottie" */ "lottie-web-vue").then(
      ({ LottieAnimation }) => LottieAnimation
    )
  )
);
app.component(
  "apexchart",
  defineAsyncComponent(() =>
    import(/* webpackChunkName: "charts-apex" */ "vue3-apexcharts").then(
      ({ default: ApexChart }) => ApexChart
    )
  )
);
app.component("TintBoltBrand", TintBoltBrand);
app.use(Toast);

(async () => {
  await waitForInitialAuthState();
  if (projectAuth.currentUser) {
    await bootstrapAuthSession(projectAuth.currentUser);
  }
  app.mount("#app");
  initGoogleAnalytics();
})();
